import { useState, type ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { useClientKpis } from '@/queries/clientKpis'
import type { ClientKpis, KpiCard, MetricRow } from '@/types/clientKpis'
import { cn } from '@/lib/utils'
import { DashCard, tdClass, thClass } from './dashboardParts'
import { BandDot, ChurnPill, PercentPill } from './HealthPills'
import { LoadError } from './LoadError'

function Change({ now, before }: { now: number | null; before: number | null }) {
  if (now == null || before == null) return null
  if (now === before) return <span className="text-etn-text-muted">No change</span>
  const up = now > before
  return (
    <span className={cn('font-semibold tabular-nums', up ? 'text-etn-success-text' : 'text-etn-danger-text')}>
      {up ? '▲' : '▼'} {Math.abs(now - before)}
    </span>
  )
}

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`

function renewalText(days: number | null | undefined) {
  if (days == null) return 'No renewal date'
  if (days === 0) return 'Renews today'
  return days > 0 ? `Renewal in ${plural(days, 'day')}` : `Renewal ended ${plural(-days, 'day')} ago`
}

const muted = 'text-etn-text-label'
const strong = 'font-semibold text-etn-text'

function History({ lastMonth, earlier, months }: { lastMonth: number | null; earlier: number | null; months: number }) {
  if (lastMonth == null && earlier == null) return <div>No history yet</div>
  return (
    <div>
      {lastMonth != null && (
        <span data-testid="kpi-last-month">
          Last month <b className={strong}>{lastMonth}%</b>
        </span>
      )}
      {lastMonth != null && earlier != null && <span className="mx-1.5">·</span>}
      {earlier != null && (
        <span data-testid="kpi-earlier">
          {months} months ago <b className={strong}>{earlier}%</b>
        </span>
      )}
    </div>
  )
}

function SummaryCard({ label, aside, children }: { label: string; aside: string; children: ReactNode }) {
  return (
    <div className="rounded-etn-card border border-etn-border-hairline bg-etn-surface px-5 py-4 shadow-etn-whisper">
      <div className={`flex items-baseline justify-between gap-3 text-xs ${muted}`}>
        <span className="font-medium">{label}</span>
        <span className="text-[11.5px]">{aside}</span>
      </div>
      {children}
    </div>
  )
}

function Summary({ kpis }: { kpis: ClientKpis }) {
  const { health, churn } = kpis
  return (
    <section data-testid="kpi-summary" className="mb-3 grid gap-3 sm:grid-cols-2">
      <SummaryCard label="Health score" aside={`Based on ${health.available} of ${health.total} KPIs`}>
        <div className="mt-2 flex h-9 items-center gap-3">
          {health.percent == null ? (
            <span className="text-[15px] font-semibold text-etn-text-soft">Not enough data</span>
          ) : (
            <>
              <BandDot band={health.band} className="size-3" />
              <span className="text-[28px] font-bold leading-none tabular-nums text-etn-text">{health.percent}%</span>
              <span className="text-base">
                <Change now={health.percent} before={health.lastMonth} />
              </span>
            </>
          )}
        </div>
        <div className={`mt-2 text-[12.5px] ${muted}`}>
          <History lastMonth={health.lastMonth} earlier={health.earlier} months={churn.fallingMonths ?? 3} />
        </div>
      </SummaryCard>
      <SummaryCard label="Churn risk" aside={renewalText(churn.daysToRenewal)}>
        <div className="mt-2 flex h-9 items-center">
          <ChurnPill churn={churn} className="px-3 py-1 text-sm" />
        </div>
        {churn.raisedFrom && (
          <div className={`mt-2 text-[12.5px] ${muted}`}>{`Raised from ${churn.raisedFrom}: the score fell ${churn.fell} points in ${churn.fallingMonths ?? 3} months.`}</div>
        )}
      </SummaryCard>
    </section>
  )
}

function Card({ kpi, selected, onSelect }: { kpi: KpiCard; selected: boolean; onSelect: () => void }) {
  const details = [
    kpi.percent != null && kpi.last != null ? `Last month ${kpi.last}%` : null,
    kpi.available < kpi.total ? `Based on ${kpi.available} of ${kpi.total} metrics` : null,
  ].filter(Boolean)
  return (
    <button
      type="button"
      data-testid="kpi-card"
      aria-pressed={selected}
      onClick={onSelect}
      className={cn(
        'flex flex-col justify-start rounded-etn-card border border-etn-border-hairline bg-etn-surface px-4 py-3.5 text-left shadow-etn-whisper hover:bg-etn-row-hover',
        selected && 'ring-2 ring-etn-accent'
      )}
    >
      <div className={`flex items-center justify-between gap-2 text-xs ${muted}`}>
        <span className="flex items-center gap-[7px]">
          <BandDot band={kpi.band} />
          {kpi.name}
        </span>
        <span className="text-[11.5px]">{kpi.healthWeight ? `${kpi.healthWeight}%` : 'Not in the health score'}</span>
      </div>
      {kpi.percent == null ? (
        <div className="mt-2 text-[15px] font-semibold text-etn-text-soft">Not enough data</div>
      ) : (
        <div className="mt-1.5 flex items-center gap-2.5">
          <span className="text-xl font-bold tabular-nums text-etn-text">{kpi.percent}%</span>
          <Change now={kpi.percent} before={kpi.last} />
        </div>
      )}
      {details.length > 0 && <div className={`mt-0.5 text-[11.5px] ${muted}`}>{details.join(' · ')}</div>}
    </button>
  )
}

function MetricRows({ rows }: { rows: MetricRow[] }) {
  return rows.map(row =>
    row.available ? (
      <tr key={row.key} className="border-b border-[var(--etn-gray-100)] last:border-0">
        <td className={cn(tdClass, 'font-medium text-etn-text')}>{row.label}</td>
        <td className={tdClass}>
          <b className="font-semibold text-etn-text">{row.figure}</b>
          <div className="text-[11.5px] text-etn-text-soft">{row.detail}</div>
        </td>
        <td className={tdClass}>
          <PercentPill percent={row.percent} band={row.band} />
        </td>
        <td className={cn(tdClass, 'text-right tabular-nums text-etn-text-label')}>{row.last == null ? '' : `${row.last}%`}</td>
        <td className={cn(tdClass, 'text-right')}>
          <Change now={row.percent} before={row.last} />
        </td>
        <td className={cn(tdClass, 'text-right tabular-nums')}>{row.weight}%</td>
      </tr>
    ) : (
      <tr key={row.key} className="border-b border-[var(--etn-gray-100)] text-etn-text-muted last:border-0">
        <td className={tdClass}>{row.label}</td>
        <td className={tdClass} colSpan={4}>
          {row.reason}
        </td>
        <td className={cn(tdClass, 'text-right tabular-nums')}>{row.weight}%</td>
      </tr>
    )
  )
}

export function KpisView({ companyId }: { companyId: string }) {
  const { data, isLoading, isError, refetch } = useClientKpis(companyId)
  const [chosen, setChosen] = useState<string | null>(null)
  if (isError && !data) return <LoadError title="KPIs didn't load" onRetry={() => refetch()} />
  if (isLoading || !data) return null
  const kpis = data.data
  const kpi = kpis.kpis.find(item => item.key === chosen)

  return (
    <div>
      <Summary kpis={kpis} />
      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {kpis.kpis.map(item => (
          <Card key={item.key} kpi={item} selected={item.key === chosen} onSelect={() => setChosen(chosen === item.key ? null : item.key)} />
        ))}
      </div>
      <DashCard
        testId="kpi-metrics"
        title={kpi ? `${kpi.name}: its metrics` : 'All metrics, by KPI'}
        subtitle={`${kpi ? 'Click the card again, or Show all KPIs, to see every KPI' : 'Click a KPI card to see only its metrics'}. Each figure becomes a success percentage with the good and bad values in Settings.`}
        right={
          kpi && (
            <Button size="sm" variant="outline" onClick={() => setChosen(null)}>
              Show all KPIs
            </Button>
          )
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className={thClass}>Metric</th>
                <th className={thClass}>Figure</th>
                <th className={thClass}>Success</th>
                <th className={cn(thClass, 'text-right')}>Last month</th>
                <th className={cn(thClass, 'text-right')}>Change</th>
                <th className={cn(thClass, 'text-right')}>Weight</th>
              </tr>
            </thead>
            <tbody>
              {kpi ? (
                <MetricRows rows={kpi.metrics} />
              ) : (
                kpis.kpis.map(item => [
                  <tr key={`${item.key}-group`} className="bg-etn-surface-sunken">
                    <td className={tdClass} colSpan={6}>
                      <span className="flex items-center gap-[7px]">
                        <BandDot band={item.band} />
                        <b className="font-semibold text-etn-text">{item.name}</b>
                        <span className="text-etn-text-label">
                          {item.percent == null ? 'Not enough data' : `${item.percent}%`}
                          {' · '}
                          {item.healthWeight ? `${item.healthWeight}% of the health score` : 'Not in the health score'}
                        </span>
                      </span>
                    </td>
                  </tr>,
                  <MetricRows key={`${item.key}-rows`} rows={item.metrics} />,
                ])
              )}
            </tbody>
          </table>
        </div>
      </DashCard>
      {kpis.otherMetrics.length > 0 && (
        <DashCard testId="kpi-other-metrics" title="Other metrics" subtitle="Shown for context; not in any KPI" className="mt-4">
          <table className="w-full">
            <tbody>
              {kpis.otherMetrics.map(row => (
                <tr key={row.key} className="border-b border-[var(--etn-gray-100)] last:border-0">
                  <td className={cn(tdClass, 'font-medium text-etn-text')}>{row.label}</td>
                  <td className={tdClass}>{row.available ? row.figure : <span className="text-etn-text-muted">{row.reason}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </DashCard>
      )}
    </div>
  )
}
